import React, { useEffect, useState, useRef, useMemo } from 'react';
import { motion, useInView } from 'framer-motion';
import axios from 'axios';
import TerminalWindow from '../components/TerminalWindow';
import { FaShieldAlt, FaCode, FaTools, FaExternalLinkAlt, FaRobot, FaServer } from 'react-icons/fa';
import { getCommandsByCategory, allCommands } from '../data/cyberCommands';
import { CommandTicker } from '../components/CommandStream';

const SkillBar = ({ name, level, delay }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <div ref={ref} className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-cyber-text">{name}</span>
        <span className="text-cyber-green">{level}%</span>
      </div>
      <div className="skill-bar">
        <div className="skill-bar-fill" style={{ width: inView ? `${level}%` : '0%', transitionDelay: `${delay}ms` }}></div>
      </div>
    </div>
  );
};

const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get('https://arap-backend.onrender.com/api/projects')
      .then(res => { setProjects(res.data); setError(null); })
      .catch(err => { console.error('Error fetching projects:', err); setError('Failed to load projects. Please try again later.'); });
  }, []);

  // Tool command showcases — real commands for each domain
  const toolShowcases = useMemo(() => [
    { title: 'nmap_recon.sh', cmds: getCommandsByCategory('nmap').slice(0, 10) },
    { title: 'metasploit_framework.rc', cmds: getCommandsByCategory('metasploit').slice(0, 10) },
    { title: 'packet_analysis.sh', cmds: getCommandsByCategory('wireshark').slice(0, 10) },
    { title: 'webapp_testing.sh', cmds: getCommandsByCategory('webapps').slice(0, 10) },
    { title: 'wireless_ops.sh', cmds: getCommandsByCategory('wireless').slice(0, 10) },
    { title: 'password_audit.sh', cmds: getCommandsByCategory('passwords').slice(0, 10) },
    { title: 'forensic_analysis.sh', cmds: getCommandsByCategory('forensics').slice(0, 10) },
    { title: 'reverse_engineering.sh', cmds: getCommandsByCategory('reverseeng').slice(0, 10) },
    { title: 'network_ops.sh', cmds: getCommandsByCategory('network').slice(0, 10) },
    { title: 'ids_ips_monitor.sh', cmds: getCommandsByCategory('ids').slice(0, 10) },
    { title: 'siem_analytics.sh', cmds: getCommandsByCategory('siem').slice(0, 10) },
    { title: 'active_directory.sh', cmds: getCommandsByCategory('activedir').slice(0, 10) },
    { title: 'mimikatz_ops.sh', cmds: getCommandsByCategory('mimikatz').slice(0, 10) },
    { title: 'vuln_scanning.sh', cmds: getCommandsByCategory('vulnscan').slice(0, 10) },
    { title: 'privesc_enum.sh', cmds: getCommandsByCategory('privesc').slice(0, 10) },
    { title: 'crypto_tools.sh', cmds: getCommandsByCategory('crypto').slice(0, 10) },
    { title: 'container_security.sh', cmds: getCommandsByCategory('containers').slice(0, 10) },
    { title: 'osint_recon.sh', cmds: getCommandsByCategory('osint').slice(0, 10) },
    { title: 'cloud_audit.sh', cmds: getCommandsByCategory('cloud').slice(0, 10) },
    { title: 'malware_lab.sh', cmds: getCommandsByCategory('malware').slice(0, 10) },
  ], []);

  const skillCategories = [
    {
      title: 'Languages & Frameworks', icon: FaCode, color: 'cyber-cyan',
      skills: [
        { name: 'JavaScript / Node.js / React', level: 92 },
        { name: 'Python', level: 90 },
        { name: 'C / C++', level: 78 },
        { name: 'Java / Kotlin', level: 75 },
        { name: 'SQL / NoSQL', level: 85 },
        { name: 'HTML / CSS / Tailwind', level: 95 },
        { name: 'Angular', level: 70 },
        { name: 'Bash / PowerShell', level: 88 },
      ],
    },
    {
      title: 'Cybersecurity', icon: FaShieldAlt, color: 'cyber-green',
      skills: [
        { name: 'Penetration Testing', level: 95 },
        { name: 'Incident Response', level: 90 },
        { name: 'Digital Forensics', level: 88 },
        { name: 'Malware Analysis', level: 85 },
        { name: 'Threat Intelligence', level: 92 },
        { name: 'Red / Blue Teaming', level: 90 },
        { name: 'Reverse Engineering', level: 82 },
        { name: 'Cryptography', level: 80 },
        { name: 'Vulnerability Assessment', level: 93 },
        { name: 'Security Operations', level: 88 },
        { name: 'Threat Modeling', level: 86 },
      ],
    },
    {
      title: 'Tools & Platforms', icon: FaTools, color: 'cyber-purple',
      skills: [
        { name: 'Kali Linux / Parrot', level: 95 },
        { name: 'Metasploit / Cobalt Strike', level: 90 },
        { name: 'Burp Suite / OWASP ZAP', level: 92 },
        { name: 'Nmap / Wireshark / TCPDump', level: 94 },
        { name: 'Snort / Suricata', level: 85 },
        { name: 'Splunk / ELK Stack', level: 88 },
        { name: 'Nessus / Qualys / OpenVAS', level: 90 },
        { name: 'BloodHound / Mimikatz', level: 82 },
        { name: 'John / Hydra / Hashcat', level: 87 },
        { name: 'Maltego / Aircrack-ng', level: 80 },
      ],
    },
  ];

  const experience = [
    {
      role: 'Lead Security Consultant', company: 'Teaburg International', time: 'March 2022 — Present', icon: FaShieldAlt,
      tasks: [
        'Led vulnerability reduction programs cutting attack surface by 60%',
        'Implemented enterprise firewall architecture and IDS/IPS solutions',
        'Managed security operations for critical infrastructure systems',
        'Conducted red team engagements and penetration testing assessments',
        'Developed incident response playbooks and trained security teams',
      ],
    },
    {
      role: 'Web Developer & Security Engineer', company: 'Autoburg International Juvenile', time: 'June 2020 — Feb 2022', icon: FaCode,
      tasks: [
        'Built secure intranet applications with defense-in-depth architecture',
        'Optimized application performance and implemented security hardening',
        'Conducted code reviews with focus on OWASP Top 10 compliance',
        'Deployed monitoring solutions and automated security scanning',
      ],
    },
  ];

  return (
    <div className="relative pt-20 pb-20">
      {/* Background command waterfall */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden command-fade-top z-0">
        <div className="flex h-full opacity-[0.025]">
          {[0, 1].map((col) => (
            <div key={col} className="flex-1 overflow-hidden">
              <div className="font-mono text-[9px] text-cyber-green leading-loose" style={{ animation: `scrollUp ${70 + col * 20}s linear infinite`, paddingTop: `${col * 500}px` }}>
                {[...allCommands, ...allCommands].map((cmd, i) => (
                  <div key={i} className="truncate px-3">$ {cmd}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* About Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-20 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <TerminalWindow title="cat about.txt">
            <div className="text-cyber-green text-xs mb-3">{'// ABOUT'}</div>
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-1">
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Kipkorir <span className="text-cyber-green">Arap</span> Bett
                </h1>
                <p className="text-cyber-text leading-relaxed mb-4">
                  Cybersecurity Specialist and Software Engineer with expertise in offensive security,
                  threat hunting, and building resilient digital infrastructure. Experienced in leading
                  security operations, conducting advanced penetration tests, and developing secure
                  applications from the ground up.
                </p>
                <p className="text-cyber-text leading-relaxed">
                  I operate at the intersection of development and security — writing code that defends,
                  building tools that detect, and breaking systems to make them stronger.
                </p>
              </div>
              <div className="md:w-64 flex-shrink-0">
                <div className="cyber-card p-4">
                  <div className="text-xs text-cyber-green mb-3 font-mono">$ cat status.json</div>
                  <div className="space-y-2 text-sm">
                    {[
                      ['role:', '"Security Engineer"', 'text-cyber-cyan'],
                      ['status:', '"active"', 'text-cyber-green'],
                      ['clearance:', '"elevated"', 'text-cyber-purple'],
                      ['uptime:', '"5+ years"', 'text-white'],
                      ['location:', '"global"', 'text-white'],
                    ].map(([key, val, color]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-cyber-muted">{key}</span>
                        <span className={color}>{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TerminalWindow>
        </motion.div>
      </section>

      {/* Skills Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-20 relative z-10">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-10">
          <h2 className="text-sm font-mono text-cyber-green mb-2">{'// SKILL MATRIX'}</h2>
          <p className="text-2xl font-bold text-white">System <span className="text-cyber-green">Capabilities</span> Scan</p>
          <div className="w-20 h-px bg-cyber-green mx-auto mt-3"></div>
        </motion.div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {skillCategories.map((category, catIdx) => (
            <motion.div key={category.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: catIdx * 0.15 }} viewport={{ once: true }}>
              <TerminalWindow title={`scan --module ${category.title.toLowerCase().replace(/ & /g, '-')}`}>
                <div className="flex items-center gap-2 mb-4">
                  <category.icon className={`text-${category.color} text-lg`} />
                  <h3 className={`text-${category.color} font-semibold`}>{category.title}</h3>
                </div>
                <div className="text-xs text-cyber-muted mb-4">[{category.skills.length}] modules detected</div>
                {category.skills.map((skill, i) => (
                  <SkillBar key={skill.name} name={skill.name} level={skill.level} delay={i * 100 + catIdx * 200} />
                ))}
              </TerminalWindow>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== FULL TOOLKIT COMMAND SHOWCASE ===== */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-20 relative z-10">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-10">
          <h2 className="text-sm font-mono text-cyber-green mb-2">{'// ARSENAL — TOOL COMMAND REFERENCE'}</h2>
          <p className="text-2xl font-bold text-white">
            <span className="text-cyber-green">260+</span> Commands Across <span className="text-cyber-cyan">20</span> Tool Categories
          </p>
          <div className="w-20 h-px bg-cyber-green mx-auto mt-3"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {toolShowcases.map((block, i) => (
            <motion.div
              key={block.title}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              viewport={{ once: true }}
            >
              <div className="terminal-window h-full">
                <div className="terminal-header py-1.5 px-3">
                  <div className="terminal-dot bg-cyber-red" style={{ width: 8, height: 8 }}></div>
                  <div className="terminal-dot bg-cyber-amber" style={{ width: 8, height: 8 }}></div>
                  <div className="terminal-dot bg-cyber-green" style={{ width: 8, height: 8 }}></div>
                  <span className="ml-2 text-[10px] text-cyber-muted">{block.title}</span>
                </div>
                <div className="p-2.5 text-[10px] leading-relaxed bg-cyber-black/40 font-mono">
                  {block.cmds.map((cmd, j) => (
                    <div key={j} className="text-cyber-green/60 truncate">
                      <span className="text-cyber-cyan/40">$</span> {cmd}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Command Ticker */}
      <div className="py-3 border-y border-cyber-green/10 bg-cyber-dark/50 overflow-hidden command-fade-sides mb-20 relative z-10">
        <CommandTicker speed={90} />
      </div>

      {/* Experience Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-20 relative z-10">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-10">
          <h2 className="text-sm font-mono text-cyber-green mb-2">{'// MISSION LOG'}</h2>
          <p className="text-2xl font-bold text-white">Operational <span className="text-cyber-green">Experience</span></p>
          <div className="w-20 h-px bg-cyber-green mx-auto mt-3"></div>
        </motion.div>
        <div className="space-y-6">
          {experience.map((job, i) => (
            <motion.div key={job.role} initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: i * 0.15 }} viewport={{ once: true }}>
              <TerminalWindow title={`cat /var/log/career/${job.company.toLowerCase().replace(/\s+/g, '_')}.log`}>
                <div className="flex items-start gap-4">
                  <div className="hidden md:flex p-3 rounded bg-cyber-green/10 border border-cyber-green/20 mt-1">
                    <job.icon className="text-xl text-cyber-green" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white">{job.role}</h3>
                    <p className="text-cyber-cyan text-sm">{job.company}</p>
                    <p className="text-cyber-muted text-xs mt-1 mb-4">{job.time}</p>
                    <div className="space-y-2">
                      {job.tasks.map((task, j) => (
                        <div key={j} className="flex items-start gap-2 text-sm">
                          <span className="text-cyber-green mt-1 flex-shrink-0">{'>'}</span>
                          <span className="text-cyber-text">{task}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TerminalWindow>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Projects Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center mb-10">
          <h2 className="text-sm font-mono text-cyber-green mb-2">{'// DEPLOYMENTS'}</h2>
          <p className="text-2xl font-bold text-white">Active <span className="text-cyber-green">Projects</span></p>
          <div className="w-20 h-px bg-cyber-green mx-auto mt-3"></div>
        </motion.div>
        {error && (
          <div className="text-center mb-6 p-4 border border-cyber-red/30 rounded bg-cyber-red/10">
            <p className="text-cyber-red text-sm font-mono">[ERROR] {error}</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.length === 0 && !error ? (
            <div className="col-span-2 text-center py-10">
              <FaServer className="text-3xl mx-auto mb-3 text-cyber-green animate-pulse" />
              <p className="text-cyber-green font-mono">{'>'} Fetching project data from server...</p>
            </div>
          ) : (
            projects.map((project, i) => (
              <motion.div key={project._id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }} viewport={{ once: true }} className="cyber-card p-6 group">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FaRobot className="text-cyber-green group-hover:text-cyber-cyan transition-colors" />
                    <h3 className="text-lg font-semibold text-white group-hover:text-cyber-green transition-colors">{project.name}</h3>
                  </div>
                  {project.url && (
                    <a href={project.url} target="_blank" rel="noopener noreferrer" className="text-cyber-muted hover:text-cyber-cyan transition-colors">
                      <FaExternalLinkAlt />
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {project.tech.split(',').map((tech) => (
                    <span key={tech.trim()} className="text-xs px-2 py-1 rounded bg-cyber-green/10 text-cyber-green border border-cyber-green/20">{tech.trim()}</span>
                  ))}
                </div>
                <p className="text-sm text-cyber-muted leading-relaxed">{project.desc}</p>
              </motion.div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
